import { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { sshApi, resourcesApi, accessRequestsApi, BASE_PATH } from '../services/api';
import { Resource, SSHAgent, AccessRequest } from '../types';
import { Terminal, Server, Play, Clock, CheckCircle, XCircle, Monitor, Copy, Lock, SquareTerminal, RotateCw } from 'lucide-react';
import { format } from 'date-fns';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table';
import PermissionGate from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { useUser } from '../contexts/UserContext';
import { Link } from 'react-router-dom';
import { Button, Card, EmptyState, IconButton, SearchField, PaginationBar, TanstackTable, Modal, PageHeader, Select, StatusBadge, StatCard } from '@/components/ds-zta';
import { useUrlState } from '@/lib/useUrlState';
import { ROWS_PER_PAGE_OPTIONS, readPage, readRows } from '@/lib/urlPaging';
import { timeValue } from '@/lib/sortValues';
import { toast } from '@/lib/toast';
import { errorMessage } from '@/lib/http';
import { copyWithToast } from '@/lib/copy';
import { BRAND } from '@/brand';

const columnHelper = createColumnHelper<Resource>();

/** Formats a timestamp, or returns "—" when it is missing or unparseable
 *  (date-fns `format` throws "Invalid time value" on an Invalid Date). "—" is
 *  the one no-value token; "Never" means never-expires only (design eval F47). */
const formatDateSafe = (value: string | number | Date | null | undefined, pattern: string) => {
  if (value === null || value === undefined || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : format(d, pattern);
};

interface SSHResourcesTableProps {
  resources: Resource[];
  sshAgents: SSHAgent[];
  getAgentStatus: (resourceId: string) => { status: string; color: string; icon: any };
  handleWebTerminal: (resource: Resource) => void;
  handleConnect: (resource: Resource) => void;
  PERMISSIONS: any;
  hasValidApproval: (resourceId: string) => boolean;
  loading: boolean;
  searchTerm: string;
}

const SSHResourcesTable: React.FC<SSHResourcesTableProps> = ({
  resources,
  sshAgents,
  getAgentStatus,
  handleWebTerminal,
  handleConnect,
  PERMISSIONS,
  hasValidApproval,
  loading,
  searchTerm,
}) => {
  const columns = useMemo<ColumnDef<Resource, any>[]>(
    () => [
      columnHelper.display({
        id: 'server',
        header: 'Server',
        meta: { sortable: true, sortValue: (r: Resource) => (r.external_id || '').toLowerCase() },
        cell: ({ row }) => (
          <div className="flex items-center">
            <div className="flex-shrink-0 h-8 w-8">
              <div className="h-8 w-8 rounded-full bg-[var(--ds-color-bg-subtle)] flex items-center justify-center">
                <Server className="h-5 w-5 text-[var(--d2d-text-muted)]" />
              </div>
            </div>
            <div className="ml-4">
              <div className="text-dense font-medium text-[var(--d2d-text-primary)] font-mono tabular-nums">
                {row.original.external_id}
              </div>
              <div className="text-dense text-[var(--d2d-text-muted)] font-mono tabular-nums">
                {row.original.labels?.hostname || 'SSH Server'}
              </div>
            </div>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'endpoint',
        header: 'Endpoint',
        meta: { sortable: true, sortValue: (r: Resource) => String(r.endpoint?.host ?? '').toLowerCase() },
        cell: ({ row }) => (
          <span className="text-dense text-[var(--d2d-text-primary)]">
            {row.original.endpoint.host}:{row.original.endpoint.port || 22}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        meta: { sortable: true, sortValue: (r: Resource) => getAgentStatus(r.external_id).status },
        cell: ({ row }) => {
          const agentStatus = getAgentStatus(row.original.external_id);
          return (
            <StatusBadge value={agentStatus.status} className="capitalize">
              {agentStatus.status}
            </StatusBadge>
          );
        },
      }),
      columnHelper.display({
        id: 'sessions',
        header: 'Sessions',
        meta: { sortable: true, sortValue: (r: Resource) => sshAgents.find(a => a.server_id === r.external_id)?.sessions || 0 },
        cell: ({ row }) => {
          const agent = sshAgents.find(a => a.server_id === row.original.external_id);
          return <span className="text-dense text-[var(--d2d-text-primary)]">{agent?.sessions || 0}</span>;
        },
      }),
      columnHelper.display({
        id: 'last_seen',
        header: 'Last Seen',
        meta: { sortable: true, sortValue: (r: Resource) => timeValue(sshAgents.find(a => a.server_id === r.external_id)?.last_seen) },
        cell: ({ row }) => {
          const agent = sshAgents.find(a => a.server_id === row.original.external_id);
          return (
            <span className="text-dense text-[var(--d2d-text-muted)]">
              {formatDateSafe(agent?.last_seen, 'MMM d, HH:mm')}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span className="block text-right">Actions</span>,
        cell: ({ row }) => {
          const agent = sshAgents.find(a => a.server_id === row.original.external_id);
          const hasAccess = hasValidApproval(row.original.id);

          return (
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleWebTerminal(row.original)}
                  disabled={!agent?.online || !hasAccess}
                  title={!hasAccess ? "Access request approval required" : "Open Web Terminal"}
                  leadingIcon={<Monitor className="h-3.5 w-3.5" />}
                >
                  Terminal
                </Button>
                <IconButton
                  size="sm"
                  aria-label="CLI"
                  tooltip={!hasAccess ? "Access request approval required" : "Create CLI Session"}
                  onClick={() => handleConnect(row.original)}
                  disabled={!agent?.online || !hasAccess}
                  icon={<Play className="h-4 w-4" />}
                />
              </div>
              {!hasAccess && agent?.online && (
                <Link to="/access-requests" className="text-micro text-[var(--d2d-text-primary)] hover:underline flex items-center gap-1">
                  <Lock className="h-3 w-3 flex-shrink-0" aria-hidden="true" />Request Access
                </Link>
              )}
            </div>
          );
        },
      }),
    ],
    [sshAgents, getAgentStatus, handleWebTerminal, handleConnect, hasValidApproval]
  );

  const table = useReactTable({
    data: resources,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const empty = searchTerm ? (
    <EmptyState
      icon={<SquareTerminal />}
      title={`No SSH servers on this page match "${searchTerm}"`}
      description="Try a different search or clear the role filter."
    />
  ) : (
    <EmptyState
      icon={<SquareTerminal />}
      title="No SSH servers yet"
      description="Add an SSH resource or connect an agent to see servers here."
    />
  );

  return (
    <div className="overflow-x-auto">
      <TanstackTable aria-label="SSH servers" table={table} loading={loading} empty={empty} />
    </div>
  );
};

// SSH CLI Commands Modal Component
interface SSHCLIModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: Resource | null;
  sshUser: string;
}

const SSHCLIModal: React.FC<SSHCLIModalProps> = ({ isOpen, onClose, resource, sshUser }) => {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  if (!isOpen || !resource) return null;

  const serverName = resource.external_id;
  const user = sshUser || 'ubuntu';

  const commands = [
    {
      title: `Authenticate with ${BRAND.name}`,
      description: `Log in to ${BRAND.name} using OIDC authentication`,
      command: `./iap auth login`,
    },
    {
      title: 'List available servers',
      description: 'List all SSH servers you have access to',
      command: `./iap ssh ls`,
    },
    {
      title: 'Connect (background)',
      description: 'Start a background tunnel as a specific user',
      command: `./iap ssh connect ${serverName} -u ${user} --background`,
    },
    {
      title: 'Connect with custom port (background)',
      description: 'Start a background tunnel on a specific local port',
      command: `./iap ssh connect ${serverName} -u ${user} --local-port 2223 --background`,
    },
    {
      title: 'Show active tunnels',
      description: 'Display all currently active SSH tunnels',
      command: `./iap ssh status`,
    },
    {
      title: 'Close tunnel',
      description: 'Close an active tunnel to the server',
      command: `./iap ssh logout ${serverName}`,
    },
  ];

  const sshClientCommands = [
    {
      title: 'SSH connect',
      description: 'Connect using standard SSH client',
      command: `ssh -p 2222 ${user}@localhost`,
    },
    {
      title: 'Copy files (SCP)',
      description: 'Copy files to the server',
      command: `scp -P 2222 file.txt ${user}@localhost:~/`,
    },
    {
      title: 'SFTP',
      description: 'Start SFTP session',
      command: `sftp -P 2222 ${user}@localhost`,
    },
  ];

  const copyToClipboard = async (command: string) => {
    if (!(await copyWithToast(command, { success: false }))) return;
    setCopiedCommand(command);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  return (
    <Modal
      dismissOnOverlay={false}
      open
      onClose={onClose}
      title="SSH CLI Commands"
      description={<>Server: <span className="font-medium text-[var(--d2d-text-secondary)]">{serverName}</span></>}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
        <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
          {/* IAP SSH Commands */}
          <div>
            <h4 className="text-sm font-medium text-[var(--d2d-text-primary)] mb-3 flex items-center">
              <Terminal className="h-4 w-4 mr-2 text-[var(--d2d-text-primary)]" />
              {BRAND.name} SSH Commands
            </h4>
            <div className="space-y-3">
              {commands.map((cmd, index) => (
                <div
                  key={index}
                  className="bg-[var(--d2d-bg-primary)] rounded-lg p-3 border border-[var(--d2d-border)]"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h5 className="text-sm font-medium text-[var(--d2d-text-primary)]">
                        {cmd.title}
                      </h5>
                      <p className="text-xs text-[var(--d2d-text-muted)] mt-0.5">
                        {cmd.description}
                      </p>
                    </div>
                    <Button
                      variant={copiedCommand === cmd.command ? 'ghost' : 'secondary'}
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => copyToClipboard(cmd.command)}
                      leadingIcon={<Copy className="h-3 w-3" />}
                    >
                      {copiedCommand === cmd.command ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                  <pre className="text-sm text-[var(--d2d-text-primary)] bg-[var(--ds-color-bg-subtle)] p-2 rounded overflow-x-auto font-mono">
                    {cmd.command}
                  </pre>
                </div>
              ))}
            </div>
          </div>

          {/* SSH Client Commands */}
          <div>
            <h4 className="text-sm font-medium text-[var(--d2d-text-primary)] mb-3 flex items-center">
              <Play className="h-4 w-4 mr-2 text-[var(--d2d-success)]" />
              After Tunnel Starts - SSH Client Commands
            </h4>
            <div className="space-y-3">
              {sshClientCommands.map((cmd, index) => (
                <div
                  key={index}
                  className="bg-[var(--d2d-bg-primary)] rounded-lg p-3 border border-[var(--d2d-border)]"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h5 className="text-sm font-medium text-[var(--d2d-text-primary)]">
                        {cmd.title}
                      </h5>
                      <p className="text-xs text-[var(--d2d-text-muted)] mt-0.5">
                        {cmd.description}
                      </p>
                    </div>
                    <Button
                      variant={copiedCommand === cmd.command ? 'ghost' : 'secondary'}
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => copyToClipboard(cmd.command)}
                      leadingIcon={<Copy className="h-3 w-3" />}
                    >
                      {copiedCommand === cmd.command ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                  <pre className="text-sm text-[var(--d2d-text-primary)] bg-[var(--ds-color-bg-subtle)] p-2 rounded overflow-x-auto font-mono">
                    {cmd.command}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-[var(--d2d-border)]">
          <div className="bg-[var(--zta-tint-info)] rounded-lg p-4">
            <h4 className="text-sm font-medium text-[var(--d2d-info)] mb-2">
              Prerequisites
            </h4>
            <ul className="text-xs text-[var(--d2d-info)] space-y-1 list-disc list-inside">
              <li>Authenticate with {BRAND.name}: <code className="bg-[var(--zta-tint-info)] px-1 rounded">iap auth login</code></li>
              <li>Default tunnel port is 2222 (use --local-port to change)</li>
            </ul>
          </div>
        </div>
    </Modal>
  );
};

export default function SSHResources() {
  // Filters, search and paging live in the URL (design eval F24). Role
  // go to the server, so changing it starts again at page 1 (F7). The
  // list endpoint takes no search param, so the search filters the loaded
  // page and says so.
  const [url, setUrl] = useUrlState({ q: '', role: '', page: '1', rows: '20' });
  const searchTerm = url.q;
  const setSearchTerm = (next: string) => setUrl({ q: next }, { replace: true });
  const selectedRole = url.role;
  const setSelectedRole = (next: string) => setUrl({ role: next, page: null });
  const page = readPage(url.page) - 1; // zero-based below, as before
  const itemsPerPage = readRows(url.rows);
  const setPage = (next: number) => setUrl({ page: next + 1 });
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [sshUser, setSshUser] = useState('ec2-user');
  const [showConnectDialog, setShowConnectDialog] = useState(false);

  const { PERMISSIONS, isAdmin } = usePermissions();
  const { secondaryRoles } = useUser();


  // Get approved access requests for non-admin users
  const { data: myApprovalsData } = useQuery(
    ['my-approvals'],
    () => accessRequestsApi.getMyApprovals(),
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  const myApprovals = myApprovalsData?.data?.items || [];

  // Get SSH resources from database
  const { data: resourcesData, isLoading: resourcesLoading, isFetching: resourcesFetching, isError: resourcesIsError, error: resourcesError, refetch: refetchResources } = useQuery(
    ['resources', 'ssh', selectedRole, page, itemsPerPage],
    () => resourcesApi.listByRole('ssh', itemsPerPage, page * itemsPerPage, selectedRole || undefined),
    { keepPreviousData: true }
  );

  // Get SSH agents from Agent Hub
  const { data: agentsData, isFetching: agentsFetching, refetch: refetchAgents } = useQuery(
    ['ssh-agents'],
    () => sshApi.getAgents(),
    {
      refetchInterval: 5000, // Refresh every 5 seconds
      keepPreviousData: true
    }
  );

  // Get Agent Hub health
  const { data: healthData } = useQuery(
    ['agent-hub-health'],
    () => sshApi.getHealth(),
    { refetchInterval: 10000 }
  );

  const sshResources = resourcesData?.data?.resources || [];
  const allAgents = agentsData?.data?.agents || [];
  // Only show SSH server agents on this page (exclude k8s_cluster, http_agent, etc.)
  const sshAgents = allAgents.filter((a: any) => a.agent_type === 'ssh_server');

  // Fetch access-status for each SSH resource to check role-based direct access
  // This covers users who have direct access via roles (no access request needed)
  const { data: accessStatusMap } = useQuery(
    ['ssh-access-status', sshResources.map(r => r.id).join(',')],
    async () => {
      const statusMap: Record<string, boolean> = {};
      await Promise.all(
        sshResources.map(async (resource) => {
          try {
            const resp = await resourcesApi.getAccessStatus(resource.id);
            statusMap[resource.id] = resp.data.has_access === true;
          } catch {
            statusMap[resource.id] = false;
          }
        })
      );
      return statusMap;
    },
    {
      enabled: sshResources.length > 0 && !isAdmin(),
      refetchInterval: 30000,
    }
  );

  // Filter resources by search term
  const filteredResources = sshResources.filter(resource =>
    resource.external_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.endpoint.host?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    Object.values(resource.labels || {}).some(label =>
      String(label).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const getAgentStatus = (resourceId: string) => {
    const agent = sshAgents.find(a => a.server_id === resourceId);
    if (!agent) return { status: 'offline', color: 'text-[var(--d2d-text-muted)]', icon: XCircle };

    if (agent.online) {
      return { status: 'online', color: 'text-[var(--d2d-success)]', icon: CheckCircle };
    } else {
      return { status: 'offline', color: 'text-[var(--danger-text)]', icon: XCircle };
    }
  };

  const handleConnect = (resource: Resource) => {
    setSelectedResource(resource);
    setShowConnectDialog(true);
  };

  const handleWebTerminal = (resource: Resource) => {
    // Check if user has access (admin or has valid approval)
    if (!hasValidApproval(resource.id)) {
      toast.error('Access denied: You need an approved access request with a valid duration to access this resource.');
      return;
    }

    // The terminal opens in its own tab. The client-side JIT duration
    // accounting that used to start here only ran when the in-page terminal
    // dialog closed, and that dialog was never opened (design eval F40): an
    // approval's remaining time is the server's to account for.
    // Open SSH terminal in a new tab (matching K8s terminal pattern)
    const defaultUser = resource.endpoint?.users?.[0] || sshUser || 'root';
    const url = `${BASE_PATH}/ssh-terminal?server=${encodeURIComponent(resource.external_id)}&resource_id=${encodeURIComponent(resource.id)}&user=${encodeURIComponent(defaultUser)}`;
    window.open(url, '_blank');
  };

  // Check if user has a valid approval for the resource (admins bypass this check)
  const hasValidApproval = (resourceId: string) => {
    // Admin users bypass access request requirements
    if (isAdmin()) {
      return true;
    }

    // Check role-based direct access (from access-status API)
    if (accessStatusMap && accessStatusMap[resourceId]) {
      return true;
    }

    // Check access request approvals
    const approval = myApprovals.find(
      (req: AccessRequest) => req.resource_id === resourceId
    );

    if (!approval) return false;

    // Check if approval status is 'approved' (not pending, expired, or revoked)
    if (approval.status !== 'approved') return false;

    // Double-check expiration time (even though backend filters, ensure client-side validation)
    if (approval.approved_at && approval.duration_seconds) {
      const approvedTime = new Date(approval.approved_at).getTime();
      const expiryTime = approvedTime + (approval.duration_seconds * 1000);
      if (Date.now() >= expiryTime) return false;
    }

    return true;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div data-tour="ssh-header">
        <PageHeader
          title="SSH Resources"
          icon={<SquareTerminal />}
          description={`Manage SSH servers and establish secure connections through ${BRAND.name}.`}
          onRefresh={() => refetchResources()}
          refreshing={resourcesFetching}
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-tour="ssh-stats">
        <StatCard icon={<Server />} label="Total Resources" value={sshResources.length} />

        <StatCard icon={<CheckCircle />} accent="success" label="Online Agents" value={sshAgents.filter(a => a.online).length} />

        <StatCard icon={<Clock />} label="Active Sessions" value={healthData?.data?.sessions || 0} />

        <StatCard icon={<Terminal />} label="Agent Hub" value={<StatusBadge shape="chip" value={healthData?.data?.status === 'healthy' ? 'healthy' : 'offline'}>{healthData?.data?.status === 'healthy' ? 'Healthy' : 'Offline'}</StatusBadge>} />
      </div>

      {/* Search and Filters */}
      <Card className="p-4 overflow-visible" data-tour="ssh-search">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <SearchField
              label="Search SSH resources on this page"
              placeholder="Search SSH resources on this page..."
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              help={(resourcesData?.data?.total ?? 0) > sshResources.length ? `Searches the ${sshResources.length} shown on this page, of ${(resourcesData?.data?.total ?? 0)}.` : undefined}
            />
          </div>
          {secondaryRoles.length > 0 && (
            <div className="sm:w-44">
              <Select
                className="gap-0" /* hidden label: no gap above the field, so it lines up with the search (F46) */
                label={<span className="sr-only">Select Role</span>}
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">Select Role</option>
                {secondaryRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </Card>

      {/* SSH Resources Table */}
      <Card className="overflow-hidden" data-tour="ssh-list">
        {resourcesIsError ? (
          <EmptyState
            icon={<SquareTerminal />}
            title="Failed to load SSH servers"
            description={errorMessage(resourcesError, 'Something went wrong while loading SSH resources.')}
            action={
              <Button variant="secondary" onClick={() => refetchResources()} leadingIcon={<RotateCw className="h-4 w-4" />}>
                Retry
              </Button>
            }
          />
        ) : (
        <SSHResourcesTable
          resources={filteredResources}
          sshAgents={sshAgents}
          getAgentStatus={getAgentStatus}
          handleWebTerminal={handleWebTerminal}
          handleConnect={handleConnect}
          PERMISSIONS={PERMISSIONS}
          hasValidApproval={hasValidApproval}
          loading={resourcesLoading}
          searchTerm={searchTerm}
        />
        )}
      </Card>

      {/* Pagination */}
      {resourcesData && resourcesData.data.total > 0 && (
        <div className="mt-4">
          {resourcesData.data.total > 0 && <PaginationBar
            page={page + 1}
            totalRows={resourcesData.data.total}
            rowsPerPage={itemsPerPage}
            onPageChange={(p) => setPage(p - 1)}
            onRowsPerPageChange={(newItemsPerPage) => setUrl({ rows: newItemsPerPage, page: null })}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />}
        </div>
      )}

      {/* SSH CLI Commands Modal */}
      <SSHCLIModal
        isOpen={showConnectDialog}
        onClose={() => {
          setShowConnectDialog(false);
          setSelectedResource(null);
        }}
        resource={selectedResource}
        sshUser={sshUser}
      />

    </div>
  );
}
